import { __assign } from '../../_virtual/_tslib.js';
import __vue_component__$1 from '../SdsSkeleton/SdsSkeletonEffect.vue.js';
import __vue_component__ from '../SdsButton/SdsButton.vue.js';
import { ScreenWidth, respondToBelow, useHover, useFocusWithin, useThrottle, useResizeTracking, useCSSVariable, useRAF, useScrollTracking, useDebounce } from '@superbet-group/web.lib.vue-utilities';
import { defineComponent, computed, ref, watch, watchEffect } from '@vue/composition-api';
import __vue_component__$4 from './SdsBaseCarouselFooter.vue.js';
import __vue_component__$5 from './SdsBaseCarouselIndicator.vue.js';
import __vue_component__$3 from './SdsBaseCarouselItem.vue.js';
import __vue_component__$2 from './SdsBaseCarouselNavigator.vue.js';
import { SdsBaseCarouselFooterSlots, SdsBaseCarouselSlots } from './types.js';
import { validators } from './validators.js';

var scrollOffsetAbsoluteTolerance = 2;
var loadingSlideTotal = 3;
var mobileBreakpoint = ScreenWidth.tSm;
var setActiveWidthDelay = 200;
var gapDesignTokenName = 'space-horizontal-between-cards';
var gapDesignTokenDefaultValue = '12px';
var gapLayoutFixedMobileDesignTokenName = '--space-between-carousel-items-mobile';
var gapLayoutFixedMobileDesignTokenDefaultValue = '6px';
var gapLayoutAutoMobileDesignTokenName = '--space-between-cards-sm';
var gapLayoutAutoMobileDesignTokenDefaultValue = '8px';
var edgeEffectClipDesignTokenName = 'space-margin';
var edgeEffectClipDesignTokenDefaultValue = '12px';
var edgeEffectGradientWidth = 64;
var navigatorSize = 32;
var navigatorGap = 4;
var scrollTrackingResumeDelay = 1000;
var resolveScrollRelativeTolerance = 0.1;
var resolveScrollAbsoluteToleranceMax = 30;
var partiallyVisibleActiveRelativeTolerance = 0.2;
var partiallyVisibleActiveAbsoluteToleranceMax = 30;
var mobileDefaultOptions = {
    showEdgeOffset: true,
    showEdgeEffect: true,
    edgeEffectVariant: 'clip',
    showNavigator: false,
    navigatorVariant: 'standalone',
    showIndicator: true,
    indicatorVariant: 'static',
    partiallyVisibleVariant: 'active',
    scrollSnap: true,
    expandOnlyItem: true,
};
function getValueFromCSSVariable(variable, variableDefault) {
    var variableValue = parseInt(variable);
    return isNaN(variableValue) ? parseInt(variableDefault) : variableValue;
}
var script = defineComponent({
    name: 'SdsBaseCarousel',
    props: {
        items: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        showEdgeOffset: {
            type: Boolean,
            required: false,
            default: false,
        },
        showEdgeEffect: {
            type: Boolean,
            required: false,
            default: false,
        },
        edgeEffectVariant: {
            type: String,
            required: false,
            default: 'clip',
        },
        showNavigator: {
            type: Boolean,
            required: false,
            default: true,
        },
        navigatorVariant: {
            type: String,
            required: false,
            default: 'inline',
        },
        showIndicator: {
            type: Boolean,
            required: false,
            default: true,
        },
        indicatorVariant: {
            type: String,
            required: false,
            default: 'interactive',
        },
        partiallyVisibleVariant: {
            type: String,
            required: false,
            default: 'active',
        },
        scrollSnap: {
            type: Boolean,
            required: false,
            default: true,
        },
        expandOnlyItem: {
            type: Boolean,
            required: false,
            default: true,
        },
        layoutVariant: {
            type: Object,
            required: false,
            default: function () { return ({
                itemsPerSlide: 3,
            }); },
            validator: function (value) {
                return validators.layoutVariant(value);
            },
        },
        responsiveLayoutVariant: {
            type: Object,
            required: false,
            default: function () { return ({}); },
            validator: function (value) {
                return Object.keys(value).every(function (breakpoint) {
                    var breakpointValue = parseInt(breakpoint);
                    var currentValue = value[breakpointValue];
                    return validators.layoutVariant(currentValue);
                });
            },
        },
        mobileOptions: {
            type: Object,
            required: false,
            default: function () { return mobileDefaultOptions; },
        },
        dataTestId: {
            type: String,
            required: false,
            default: undefined,
        },
    },
    emits: [
        'slideChanged',
        'navigatorPreviousClick',
        'navigatorNextClick',
        'indicatorClick',
        'scrollPrevious',
        'scrollNext',
        'hover',
        'focusWithin',
        'itemClick',
        'itemHover',
        'itemFocusWithin',
    ],
    setup: function (props, _a) {
        var emit = _a.emit;
        var activeMobileOptions = computed(function () { return (__assign(__assign({}, mobileDefaultOptions), props.mobileOptions)); });
        var activeShowEdgeOffset = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.showEdgeOffset : props.showEdgeOffset; });
        var activeShowEdgeEffect = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.showEdgeEffect : props.showEdgeEffect; });
        var activeEdgeEffectVariant = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.edgeEffectVariant : props.edgeEffectVariant; });
        var activeShowNavigator = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.showNavigator : props.showNavigator; });
        var activeNavigatorVariant = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.navigatorVariant : props.navigatorVariant; });
        var activeShowIndicator = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.showIndicator : props.showIndicator; });
        var activeIndicatorVariant = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.indicatorVariant : props.indicatorVariant; });
        var activePartiallyVisibleVariant = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.partiallyVisibleVariant : props.partiallyVisibleVariant; });
        var activeScrollSnap = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.scrollSnap : props.scrollSnap; });
        var activeExpandOnlyItem = computed(function () { return respondToBelow(mobileBreakpoint) ? activeMobileOptions.value.expandOnlyItem : props.expandOnlyItem; });
        var baseCarouselRef = ref(null);
        useHover(baseCarouselRef, function (state) { return emit('hover', state); });
        useFocusWithin(baseCarouselRef, function (state) { return emit('focusWithin', state); });
        var activeWidth = ref(0);
        var setActiveWidthThrottle = useThrottle(function (width) {
            activeWidth.value = width;
        }, setActiveWidthDelay);
        var width = useResizeTracking(baseCarouselRef).width;
        watch(width, function (newWidth, oldWidth) {
            if (oldWidth === 0) {
                activeWidth.value = newWidth;
                return;
            }
            scrollTrackingPauseAndRestart();
            setActiveWidthThrottle(newWidth);
        });
        var sortedResponsiveLayoutVariantBreakpoints = computed(function () { return Object.keys(props.responsiveLayoutVariant).sort(function (a, b) { return parseInt(a) - parseInt(b); }); });
        var activeLayoutVariant = ref(__assign({}, props.layoutVariant));
        watchEffect(function () {
            var layoutVariant = __assign({}, props.layoutVariant);
            if (activeWidth.value > 0) {
                var activeBreakpointValue_1 = null;
                sortedResponsiveLayoutVariantBreakpoints.value.forEach(function (breakpoint) {
                    var breakpointValue = parseInt(breakpoint);
                    if (activeWidth.value >= breakpointValue) {
                        activeBreakpointValue_1 = breakpointValue;
                    }
                });
                if (activeBreakpointValue_1 !== null) {
                    layoutVariant = __assign({}, props.responsiveLayoutVariant[activeBreakpointValue_1]);
                }
            }
            Object.assign(activeLayoutVariant.value, layoutVariant);
        });
        var gapDesignToken = useCSSVariable(gapDesignTokenName, gapDesignTokenDefaultValue);
        var gapLayoutFixedMobileDesignToken = useCSSVariable(gapLayoutFixedMobileDesignTokenName, gapLayoutFixedMobileDesignTokenDefaultValue);
        var gapLayoutAutoMobileDesignToken = useCSSVariable(gapLayoutAutoMobileDesignTokenName, gapLayoutAutoMobileDesignTokenDefaultValue);
        var gapValue = computed(function () {
            if (respondToBelow(mobileBreakpoint)) {
                if (activeLayoutVariant.value.itemsPerSlide === 'auto') {
                    return getValueFromCSSVariable(gapLayoutAutoMobileDesignToken.value, gapLayoutAutoMobileDesignTokenDefaultValue);
                }
                return getValueFromCSSVariable(gapLayoutFixedMobileDesignToken.value, gapLayoutFixedMobileDesignTokenDefaultValue);
            }
            return getValueFromCSSVariable(gapDesignToken.value, gapDesignTokenDefaultValue);
        });
        var edgeEffectDesignToken = useCSSVariable(edgeEffectClipDesignTokenName, edgeEffectClipDesignTokenDefaultValue);
        var edgeEffectValue = computed(function () { return getValueFromCSSVariable(edgeEffectDesignToken.value, edgeEffectClipDesignTokenDefaultValue); });
        var scrollPadding = computed(function () {
            if (!activeShowEdgeEffect.value) {
                return 0;
            }
            return activeEdgeEffectVariant.value === 'clip' ? edgeEffectValue.value : edgeEffectGradientWidth;
        });
        var activeItemsPerSlide = computed(function () {
            if (activeLayoutVariant.value.itemsPerSlide === 'auto') {
                var autoItemsPerSlide = Math.floor((activeWidth.value - scrollPadding.value * 2 + gapValue.value) / (activeLayoutVariant.value.itemsPerSlideAutoWidth + gapValue.value));
                return autoItemsPerSlide > 0 ? autoItemsPerSlide : 1;
            }
            return activeLayoutVariant.value.itemsPerSlide;
        });
        var edgeOffsetWidth = computed(function () { return activeShowEdgeOffset.value ? scrollPadding.value : 0; });
        var edgeActiveItemsPerSlide = computed(function () {
            if (activeLayoutVariant.value.itemsPerSlide === 'auto') {
                var autoEdgeActiveItemsPerSlide = Math.floor((activeWidth.value - scrollPadding.value - edgeOffsetWidth.value + gapValue.value) / (activeLayoutVariant.value.itemsPerSlideAutoWidth + gapValue.value));
                return autoEdgeActiveItemsPerSlide > 0 ? autoEdgeActiveItemsPerSlide : 1;
            }
            return activeItemsPerSlide.value;
        });
        var loadingIdGenerator = 0;
        var activeItems = computed(function () {
            if (props.isLoading) {
                // eslint-disable-next-line no-plusplus
                return Array.from({ length: edgeActiveItemsPerSlide.value + 2 }, function () { return ({ id: "SdsBaseCarouselItem-".concat(loadingIdGenerator++) }); });
            }
            return props.items;
        });
        var activeSlide = ref(0);
        var slideTotal = computed(function () {
            if (activeItemsPerSlide.value !== edgeActiveItemsPerSlide.value && activeItems.value.length >= edgeActiveItemsPerSlide.value) {
                return Math.ceil((activeItems.value.length - edgeActiveItemsPerSlide.value * 2) / activeItemsPerSlide.value) + 2;
            }
            return Math.ceil(activeItems.value.length / activeItemsPerSlide.value);
        });
        var lastSnapItemIndex = computed(function () { return activeItems.value.length - edgeActiveItemsPerSlide.value; });
        function checkSnapIndex(index) {
            if (!activeScrollSnap.value) {
                return false;
            }
            if (index === 0 || index === lastSnapItemIndex.value || (index < lastSnapItemIndex.value && index === edgeActiveItemsPerSlide.value)) {
                return true;
            }
            if (index < edgeActiveItemsPerSlide.value || index > lastSnapItemIndex.value) {
                return false;
            }
            return (index - edgeActiveItemsPerSlide.value) % activeItemsPerSlide.value === 0;
        }
        var itemWidth = computed(function () {
            if (activeLayoutVariant.value.itemsPerSlide === 'auto') {
                return activeLayoutVariant.value.itemsPerSlideAutoWidth;
            }
            if (activeWidth.value <= 0) {
                return 0;
            }
            return (activeWidth.value - scrollPadding.value * 2 + gapValue.value) / activeItemsPerSlide.value - gapValue.value;
        });
        var itemOffsetWidth = computed(function () { return itemWidth.value + gapValue.value; });
        var lastSlideOffset = computed(function () { return activeItems.value.length * itemOffsetWidth.value - gapValue.value - activeWidth.value + 2 * edgeOffsetWidth.value; });
        function getSlideScrollOffset(slide) {
            if (slide <= 0) {
                return 0;
            }
            if (slide >= slideTotal.value - 1) {
                return lastSlideOffset.value + scrollOffsetAbsoluteTolerance;
            }
            var firstSlideWidth = itemOffsetWidth.value * edgeActiveItemsPerSlide.value + edgeOffsetWidth.value;
            var slideWidth = itemOffsetWidth.value * activeItemsPerSlide.value;
            return slideWidth * (slide - 1) + firstSlideWidth - scrollPadding.value;
        }
        var updateActiveSlideRAF = useRAF(function (slide) {
            var _a;
            (_a = baseCarouselScrollerRef.value) === null || _a === void 0 ? void 0 : _a.scrollTo({
                left: getSlideScrollOffset(slide),
            });
        });
        function updateActiveSlide(requestSlide, eventOrigin) {
            if (props.isLoading || !slideTotal.value) {
                return;
            }
            var newSlide = requestSlide;
            if (requestSlide < 0) {
                newSlide = 0;
            }
            else if (requestSlide > slideTotal.value - 1) {
                newSlide = slideTotal.value - 1;
            }
            switch (eventOrigin) {
                case 'configChange':
                case 'navigatorPrevious':
                case 'navigatorNext':
                case 'indicator':
                    scrollTrackingPauseAndRestart();
                    updateActiveSlideRAF(newSlide);
                    break;
            }
            if (newSlide === activeSlide.value) {
                return;
            }
            activeSlide.value = newSlide;
            emit('slideChanged', newSlide);
            switch (eventOrigin) {
                case 'navigatorPrevious':
                    emit('navigatorPreviousClick', newSlide);
                    break;
                case 'navigatorNext':
                    emit('navigatorNextClick', newSlide);
                    break;
                case 'indicator':
                    emit('indicatorClick', newSlide);
                    break;
                case 'scrollPrevious':
                    emit('scrollPrevious', newSlide);
                    break;
                case 'scrollNext':
                    emit('scrollNext', newSlide);
                    break;
            }
        }
        var baseCarouselScrollerComponentRef = ref(null);
        var baseCarouselScrollerRef = computed(function () {
            var _a;
            var baseCarouselScrollerElement = (_a = baseCarouselScrollerComponentRef.value) === null || _a === void 0 ? void 0 : _a.$el;
            return baseCarouselScrollerElement instanceof HTMLUListElement ? baseCarouselScrollerElement : null;
        });
        function emitItemEvent(_a, index, eventOrigin) {
            var state = _a.state, element = _a.element;
            if (props.isLoading) {
                return;
            }
            var payload = {
                index: index,
                element: element,
                containerElement: baseCarouselScrollerRef.value,
            };
            switch (eventOrigin) {
                case 'click':
                    emit('itemClick', payload);
                    break;
                case 'hover':
                    emit('itemHover', __assign({ state: state }, payload));
                    break;
                case 'focusWithin':
                    emit('itemFocusWithin', __assign({ state: state }, payload));
                    break;
            }
        }
        var _b = useScrollTracking(baseCarouselScrollerRef), isScrolling = _b.isScrolling, scrollOffset = _b.scrollOffset, status = _b.status, pause = _b.pause, resume = _b.resume;
        var scrollTrackingResumeDebounce = useDebounce(function () {
            resume();
        }, scrollTrackingResumeDelay);
        function scrollTrackingPauseAndRestart() {
            pause();
            scrollTrackingResumeDebounce();
        }
        watchEffect(function () {
            if (!isScrolling.value) {
                scrollTrackingResumeDebounce.flush();
            }
        });
        watchEffect(function () {
            if (status.value !== 'running') {
                return;
            }
            var itemWidthTolerance = itemWidth.value * resolveScrollRelativeTolerance;
            itemWidthTolerance = itemWidthTolerance < resolveScrollAbsoluteToleranceMax ? itemWidthTolerance : resolveScrollAbsoluteToleranceMax;
            var slideScrollOffset = getSlideScrollOffset(activeSlide.value);
            if (scrollOffset.value < slideScrollOffset + itemWidthTolerance
                && scrollOffset.value > slideScrollOffset - itemWidthTolerance) {
                return;
            }
            var previousSlideScrollOffset = getSlideScrollOffset(activeSlide.value - 1);
            if (scrollOffset.value < previousSlideScrollOffset + itemWidthTolerance) {
                updateActiveSlide(activeSlide.value - 1, 'scrollPrevious');
                return;
            }
            var nextSlideScrollOffset = getSlideScrollOffset(activeSlide.value + 1);
            if (scrollOffset.value > nextSlideScrollOffset - itemWidthTolerance) {
                updateActiveSlide(activeSlide.value + 1, 'scrollNext');
            }
        });
        watch([function () { return activeLayoutVariant.value.itemsPerSlide; }, function () { return activeLayoutVariant.value.itemsPerSlideAutoWidth; }], function (_a, _b) {
            var newItemsPerSlide = _a[0], newItemsPerSlideAutoWidth = _a[1];
            var oldItemsPerSlide = _b[0], oldItemsPerSlideAutoWidth = _b[1];
            if (!props.isLoading && scrollOffset.value > 0
                && (newItemsPerSlide !== oldItemsPerSlide) || (newItemsPerSlideAutoWidth !== oldItemsPerSlideAutoWidth)) {
                updateActiveSlide(0, 'configChange');
            }
        });
        var navigatorActive = ref({
            previous: false,
            next: false,
        });
        watchEffect(function () {
            if (slideTotal.value <= 1) {
                Object.assign(navigatorActive.value, {
                    previous: false,
                    next: false,
                });
                return;
            }
            if (activeScrollSnap.value) {
                Object.assign(navigatorActive.value, {
                    previous: activeSlide.value > 0,
                    next: activeSlide.value < slideTotal.value - 1,
                });
                return;
            }
            Object.assign(navigatorActive.value, {
                previous: scrollOffset.value > 0,
                next: scrollOffset.value < lastSlideOffset.value - scrollOffsetAbsoluteTolerance,
            });
        });
        function checkVisibility(index) {
            var visibleStart = scrollOffset.value;
            var visibleEnd = visibleStart + activeWidth.value;
            if (visibleStart > 0) {
                visibleStart += scrollPadding.value;
            }
            if (scrollOffset.value < lastSlideOffset.value - scrollOffsetAbsoluteTolerance) {
                visibleEnd -= scrollPadding.value;
            }
            var itemStart = index * itemOffsetWidth.value + edgeOffsetWidth.value;
            var itemEnd = itemStart + itemWidth.value;
            if (itemStart >= Math.floor(visibleStart) && itemEnd <= Math.ceil(visibleEnd)) {
                return 'full';
            }
            var itemWidthTolerance = itemWidth.value * partiallyVisibleActiveRelativeTolerance;
            itemWidthTolerance = itemWidthTolerance < partiallyVisibleActiveAbsoluteToleranceMax ? itemWidthTolerance : partiallyVisibleActiveAbsoluteToleranceMax;
            visibleStart += itemWidthTolerance;
            if (itemStart < visibleStart && itemEnd > visibleStart) {
                return 'partialPrevious';
            }
            visibleEnd -= itemWidthTolerance;
            if (itemStart < visibleEnd && itemEnd > visibleEnd) {
                return 'partialNext';
            }
            return 'notVisible';
        }
        function checkInertIndex(index) {
            if (isScrolling.value) {
                return true;
            }
            var visibility = checkVisibility(index);
            return visibility === 'notVisible' || (activePartiallyVisibleVariant.value === 'inert') && (visibility === 'partialPrevious' || visibility === 'partialNext');
        }
        var baseCarouselSkipLinkStartComponentRef = ref(null);
        var baseCarouselSkipLinkEndComponentRef = ref(null);
        var skipLinkRAF = useRAF(function (element) {
            element.focus();
        });
        function skipLink(eventOrigin) {
            var _a, _b;
            switch (eventOrigin) {
                case 'start':
                    var skipLinkEndElement = (_a = baseCarouselSkipLinkEndComponentRef.value) === null || _a === void 0 ? void 0 : _a.$el;
                    if (skipLinkEndElement instanceof HTMLElement) {
                        skipLinkRAF(skipLinkEndElement);
                    }
                    break;
                case 'end':
                    var skipLinkStartElement = (_b = baseCarouselSkipLinkStartComponentRef.value) === null || _b === void 0 ? void 0 : _b.$el;
                    if (skipLinkStartElement instanceof HTMLElement) {
                        skipLinkRAF(skipLinkStartElement);
                    }
                    break;
            }
        }
        var CSSVariables = computed(function () { return ({
            '--sds-base-carousel-edge-width': "".concat(edgeEffectGradientWidth, "px"),
            '--sds-base-carousel-scroller-gap': "".concat(gapValue.value, "px"),
            '--sds-base-carousel-scroller-padding-inline': "".concat(edgeOffsetWidth.value, "px"),
            '--sds-base-carousel-scroller-scroll-padding': "".concat(scrollPadding.value, "px"),
            '--sds-base-carousel-item-width': activeExpandOnlyItem.value && activeItems.value.length === 1 ? '100%' : "".concat(itemWidth.value, "px"),
            '--sds-base-carousel-navigator-gap': "".concat(navigatorGap, "px"),
            '--sds-base-carousel-navigator-size': "".concat(navigatorSize, "px"),
            '--sds-base-carousel-navigator-offset': "".concat((navigatorGap + navigatorSize) * 2, "px"),
        }); });
        return {
            activeShowEdgeOffset: activeShowEdgeOffset,
            activeShowEdgeEffect: activeShowEdgeEffect,
            activeEdgeEffectVariant: activeEdgeEffectVariant,
            activeShowNavigator: activeShowNavigator,
            activeNavigatorVariant: activeNavigatorVariant,
            activeShowIndicator: activeShowIndicator,
            activeIndicatorVariant: activeIndicatorVariant,
            activeScrollSnap: activeScrollSnap,
            baseCarouselRef: baseCarouselRef,
            activeItemsPerSlide: activeItemsPerSlide,
            edgeActiveItemsPerSlide: edgeActiveItemsPerSlide,
            activeItems: activeItems,
            activeSlide: activeSlide,
            slideTotal: slideTotal,
            checkSnapIndex: checkSnapIndex,
            itemWidth: itemWidth,
            updateActiveSlide: updateActiveSlide,
            baseCarouselScrollerComponentRef: baseCarouselScrollerComponentRef,
            emitItemEvent: emitItemEvent,
            navigatorActive: navigatorActive,
            checkInertIndex: checkInertIndex,
            baseCarouselSkipLinkStartComponentRef: baseCarouselSkipLinkStartComponentRef,
            baseCarouselSkipLinkEndComponentRef: baseCarouselSkipLinkEndComponentRef,
            skipLink: skipLink,
            CSSVariables: CSSVariables,
            loadingSlideTotal: loadingSlideTotal,
            SdsBaseCarouselFooterSlots: SdsBaseCarouselFooterSlots,
            SdsBaseCarouselSlots: SdsBaseCarouselSlots,
        };
    },
    components: {
        SdsButton: __vue_component__,
        SdsSkeletonEffect: __vue_component__$1,
        SdsBaseCarouselNavigator: __vue_component__$2,
        SdsBaseCarouselItem: __vue_component__$3,
        SdsBaseCarouselFooter: __vue_component__$4,
        SdsBaseCarouselIndicator: __vue_component__$5,
    },
});

export default script;
